/**
 * originalsService.ts - Service for handling aggregate "originals" content
 * 
 * This service handles the "originals" endpoint which returns a combination of all content types
 * (cinema, content, podcasts, animations, sneak peeks) for the aggregate originals page view.
 * 
 * Note: "Originals" is not a content type itself, but an aggregate view of all original content.
 */
// src/services/originalsService.ts
import unifiedAuth from '../utils/unifiedAuth';
import type { UnifiedContentItem } from '../types/unifiedContent';
import { config } from '../config';

const API_BASE_URL = config.apiBaseUrl;

export interface OriginalItem {
  id: string;
  title: string;
  description: string;
  short_description?: string;
  thumbnail: string;
  contentType: 'CINEMA' | 'CONTENT' | 'ANIMATION' | 'SNEAK_PEEK' | 'PODCAST';
  category: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  duration?: number;
  viewCount: number;
  likeCount: number;
  isFeatured: boolean;
  isTrending: boolean;
  author: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

export interface OriginalsPaginatedResponse {
  success: boolean;
  message: string;
  data: {
    content: OriginalItem[];
    totalElements: number;
    totalPages: number;
    currentPage: number;
    size: number;
    first: boolean;
    last: boolean;
  };
}

export interface OriginalsFilters {
  page?: number;
  size?: number;
  category?: string;
  contentType?: 'CINEMA' | 'CONTENT' | 'ANIMATION' | 'SNEAK_PEEK' | 'PODCAST';
  search?: string;
  featured?: boolean;
  trending?: boolean;
}

class OriginalsService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Create headers object
    const headers: Record<string, string> = {};
    
    // Authorization is attached by unifiedAuth.fetch, which refreshes on expiry.

    // Only add Content-Type if it's not FormData
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Merge with any headers from options
    if (options.headers) {
      const optionHeaders = options.headers as Record<string, string>;
      Object.entries(optionHeaders).forEach(([key, value]) => {
        if (key.toLowerCase() === 'content-type' && options.body instanceof FormData) {
          return;
        }
        headers[key] = value;
      });
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    // Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch()
    const response = await unifiedAuth.fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Get all originals content with filters
  async getOriginals(filters: OriginalsFilters = {}): Promise<OriginalsPaginatedResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    return this.makeRequest(`/content/originals${queryString ? `?${queryString}` : ''}`);
  }

  // Get featured originals
  async getFeaturedOriginals(): Promise<OriginalItem[]> {
    const response = await this.getOriginals({ featured: true, size: 10 });
    return response.data.content;
  }

  // Get trending originals
  async getTrendingOriginals(): Promise<OriginalItem[]> {
    const response = await this.getOriginals({ trending: true, size: 10 });
    return response.data.content;
  }

  // Get originals by content type
  async getOriginalsByType(contentType: 'CINEMA' | 'CONTENT' | 'ANIMATION' | 'SNEAK_PEEK' | 'PODCAST', filters: Omit<OriginalsFilters, 'contentType'> = {}): Promise<OriginalsPaginatedResponse> {
    return this.getOriginals({ ...filters, contentType });
  }

  // Search originals
  async searchOriginals(query: string, filters: Omit<OriginalsFilters, 'search'> = {}): Promise<OriginalsPaginatedResponse> {
    return this.getOriginals({ ...filters, search: query });
  }

  // Get originals stats
  async getOriginalsStats(): Promise<{
    totalOriginals: number;
    totalCinema: number;
    totalContent: number;
    totalAnimations: number;
    totalSneakPeeks: number;
    totalPodcasts: number;
    totalViews: number;
    totalLikes: number;
    featuredOriginals: OriginalItem[];
    trendingOriginals: OriginalItem[];
    recentOriginals: OriginalItem[];
  }> {
    // This would need to be implemented in the backend
    // For now, we'll aggregate from the originals endpoint
    const [featured, trending, recent] = await Promise.all([
      this.getFeaturedOriginals(),
      this.getTrendingOriginals(),
      this.getOriginals({ size: 10 })
    ]);

    return {
      totalOriginals: recent.data.totalElements,
      totalCinema: 0, // Would need separate endpoint
      totalContent: 0, // Would need separate endpoint
      totalAnimations: 0, // Would need separate endpoint
      totalSneakPeeks: 0, // Would need separate endpoint
      totalPodcasts: 0, // Would need separate endpoint
      totalViews: recent.data.content.reduce((sum, item) => sum + item.viewCount, 0),
      totalLikes: recent.data.content.reduce((sum, item) => sum + item.likeCount, 0),
      featuredOriginals: featured,
      trendingOriginals: trending,
      recentOriginals: recent.data.content,
    };
  }

  // ===== COMBINED CONTENT + SERIES METHODS =====
  
  async getOriginalsCombined(filters: OriginalsFilters = {}): Promise<UnifiedContentItem[]> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          let backendKey = key;
          let backendValue = value.toString();
          
          if (key === 'size') {
            backendKey = 'size';
          } else if (key === 'page') {
            backendValue = Math.max(0, parseInt(value.toString()) - 1).toString();
          }
          
          params.append(backendKey, backendValue);
        }
      }
    });

    const queryString = params.toString();
    const response = await this.makeRequest<{ success: boolean; message: string; data: UnifiedContentItem[] }>(
      `/content/originals-combined${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
    return response.data || [];
  }
}

export const originalsService = new OriginalsService();
export default originalsService;